// react
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

// context
import { ContextProject, ContextUserProjectPermissions } from '../Project.context';
import { ContextUser, ContextUserEntity } from '../../../../App.context';

// mui
import { Box, Container, Grid, Typography } from '@mui/material';

// icons
import { Settings as IconSettings } from '@mui/icons-material';

// ui
import { Tabs } from '../../../ui/Tabs/Tabs';

// v2 listing header
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// types
import { entityTypesDefault } from '../../../../@types/unions';

// config
import { featureFlags } from '../../../../config/featureFlags';

export const Settings = () => {
	// hooks
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const isWfa = user?.role === 'WFA';

	// tabs
	const tabs = useMemo(() => {
		const tabs = [];

		if (isWfa || featureFlags['IFRS']) {
			tabs.push({
				component: RouterLink,
				label: t('project'),
				to: `/project/${project?.idWbg}/settings/project`,
				value: 'project',
			});
		}

		if (userProjectPermissions?.component?.create) {
			tabs.push({
				component: RouterLink,
				label: t('components'),
				to: `/project/${project?.idWbg}/settings/components`,
				value: 'components',
			});
		}

		if (userProjectPermissions?.category?.create) {
			tabs.push({
				component: RouterLink,
				label: t('categories'),
				to: `/project/${project?.idWbg}/settings/categories`,
				value: 'categories',
			});
		}

		if (userProjectPermissions?.expenseType?.create) {
			tabs.push({
				component: RouterLink,
				label: t('expenseTypes'),
				to: `/project/${project?.idWbg}/settings/expense-types`,
				value: 'expenseTypes',
			});
		}

		if (userProjectPermissions?.tag?.create) {
			tabs.push({
				component: RouterLink,
				label: t('tags'),
				to: `/project/${project?.idWbg}/settings/tags`,
				value: 'tags',
			});
		}

		if (
			userEntity &&
			entityTypesDefault.some((type) => type === userEntity?.type) &&
			userProjectPermissions?.form?.update
		) {
			tabs.push({
				component: RouterLink,
				label: t('forms'),
				to: `/project/${project?.idWbg}/settings/forms`,
				value: 'forms',
			});
		}

		if (userProjectPermissions?.benefit?.create) {
			tabs.push({
				component: RouterLink,
				label: t('nonCashBenefits'),
				to: `/project/${project?.idWbg}/settings/benefits`,
				value: 'benefits',
			});
		}

		if (featureFlags['IFRS'] && userProjectPermissions?.cashInMethod?.create) {
			tabs.push({
				component: RouterLink,
				label: t('cashInMethods'),
				to: `/project/${project?.idWbg}/settings/cash-in-methods`,
				value: 'cashInMethods',
			});
		}

		if (featureFlags['IFRS'] && userProjectPermissions?.reconciliationItem?.create) {
			tabs.push({
				component: RouterLink,
				label: t('reconciliationItems'),
				to: `/project/${project?.idWbg}/settings/reconciliation-items`,
				value: 'reconciliationItems',
			});
		}

		if (project?.version === '2' && userProjectPermissions?.customField?.create) {
			tabs.push({
				component: RouterLink,
				label: t('customFields'),
				to: `/project/${project?.idWbg}/settings/custom-fields`,
				value: 'customFields',
			});
		}

		if (user?.id) {
			tabs.push({
				component: RouterLink,
				label: t('notifications'),
				to: `/project/${project?.idWbg}/settings/notifications`,
				value: 'notifications',
			});
		}

		if (user?.role === 'WFA' && featureFlags['V2_ONBOARDING']) {
			tabs.push({
				component: RouterLink,
				label: t('v2Onboarding'),
				to: `/project/${project?.idWbg}/settings/v2`,
				value: 'v2',
			});
		}

		if (userProjectPermissions?.program?.create) {
			tabs.push({
				component: RouterLink,
				label: t('program'),
				to: `/project/${project?.idWbg}/settings/program`,
				value: 'program',
			});
		}

		if (user?.role === 'WFA') {
			tabs.push({
				component: RouterLink,
				label: t('permissions'),
				to: `/project/${project?.idWbg}/settings/permissions`,
				value: 'permissions',
			});
		}

		return tabs;
	}, [isWfa, project, t, user, userEntity, userProjectPermissions]);

	// defaults
	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('settings/benefits')) {
				return 'benefits';
			} else if (pathname.includes('settings/cash-in-methods')) {
				return 'cashInMethods';
			} else if (pathname.includes('settings/categories')) {
				return 'categories';
			} else if (pathname.includes('settings/components')) {
				return 'components';
			} else if (pathname.includes('settings/custom-fields')) {
				return 'customFields';
			} else if (pathname.includes('settings/expense-types')) {
				return 'expenseTypes';
			} else if (pathname.includes('settings/form')) {
				return 'forms';
			} else if (pathname.includes('settings/notifications')) {
				return 'notifications';
			} else if (pathname.includes('settings/permissions')) {
				return 'permissions';
			} else if (pathname.includes('settings/program')) {
				return 'program';
			} else if (pathname.includes('settings/project')) {
				return 'project';
			} else if (pathname.includes('settings/reconciliation-items')) {
				return 'reconciliationItems';
			} else if (pathname.includes('settings/tags')) {
				return 'tags';
			} else if (pathname.includes('settings/v2')) {
				return 'v2';
			} else {
				return tabs[0].value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [tabValue, setTabValue] = useState(defaultTabValue);

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// navigate to first tab if path is not set
	useEffect(() => {
		const segments = location.pathname.split('/');
		if (segments.length < 5) {
			navigate(tabs[0].to);
		}
	}, [location, navigate, tabs]);

	return (
		<>
			{project?.version === '2' && (
				<HeaderListingPage
					icon={IconSettings}
					title={t('settings')}
					subtitle={t('settings.subtitle')}
					variant="condition"
				/>
			)}
			<Box sx={{ pt: project?.version === '2' ? 3 : 0 }}>
				<Grid container spacing={2}>
					{project?.version !== '2' && (
						<Grid item xs={12}>
							<Container maxWidth={false}>
								<Typography variant="h2">{t('settings')}</Typography>
							</Container>
						</Grid>
					)}
					<Grid item xs={12}>
						<Tabs
							hasBorder={false}
							onChange={handleChangeTab}
							scrollButtons="auto"
							tabs={tabs}
							value={tabValue}
						/>
					</Grid>
					<Grid item xs={12}>
						<Outlet />
					</Grid>
				</Grid>
			</Box>
		</>
	);
};
